/* eslint-disable no-bitwise */
/* eslint-disable no-undefined */
/* eslint-disable eqeqeq */
/**
 * Few helper functions that can be utilized throughout the code.
 *
 * Note: Keep this file small and clean.
 */

import { System, LanderCookie, QueryParam } from "./Constants";
import { LanderConfig } from "../types/LanderConfig";

interface TimeZoneObject {
  offset: number;
  city: string;
}

/**
 * Return domain name in the order:
 *  - query parameter
 *  - hostname from location
 * @returns string
 */
export function getDomainName(queryConfig: Record<string, any>): string {
  // check domain in query param
  if (Object.prototype.hasOwnProperty.call(queryConfig, QueryParam.DOMAIN)) {
    return queryConfig[QueryParam.DOMAIN]!;
  }

  // return hostname from the url
  return window.location.hostname;
}

/**
 * Encode query parameter
 */
function urlencode(str: string | number | boolean): string {
  str = (str + "").toString();

  return encodeURIComponent(str)
    .replace("!", "%21")
    .replace("'", "%27")
    .replace("(", "%28")
    .replace(")", "%29")
    .replace("*", "%2A");
}

/**
 * Returns HTTP query string from Object. Ignores null values.
 * Eg. key=val&key2=val2
 * @param obj
 */
export function objectToQueryStringExcludeNull(
  obj: Record<string, any>
): string {
  // https://eslint.org/docs/rules/guard-for-in
  const kv: string[] = [];
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (typeof obj[key] === "object") {
        kv.push(
          key + "=" + urlencode(objectToQueryStringExcludeNull(obj[key]))
        );
      } else if (obj[key] !== null) {
        kv.push(key + "=" + urlencode(obj[key]));
      }
    }
  }
  return kv.length > 0 ? kv.join("&") : "";
}

/**
 * Deep copy object using iteration
 * @param src
 */
export function copyObject<T extends Record<string, any>>(src: T): T {
  const target = {} as T;
  for (const prop in src) {
    if (Object.prototype.hasOwnProperty.call(src, prop)) {
      target[prop] = src[prop];
    }
  }
  return target;
}

/**
 * Returns true if the object is empty or undefined
 * @param obj
 * @returns {boolean}
 */
export function isEmptyOrUndefined(obj: any): boolean {
  if (obj === null || typeof obj === "undefined") {
    return true;
  }
  return Object.keys(obj).length === 0;
}

/**
 * If the variable is an array, the function will returned the values joined by the separator argument
 * (if the separator is not provided, then the values are joined without any separator).
 * @param variable
 * @param separator
 * @returns {*}
 */
export function joinIfArray(
  variable: any,
  separator = ","
): string | undefined {
  if (variable === null || typeof variable === "undefined") {
    return undefined;
  }
  if (Array.isArray(variable)) {
    return variable.join(separator);
  }
  return variable as string;
}

/**
 * If the argument is an array, returns its length, or else returns 1.
 * @param arg
 * @returns {int}
 */
export function getLength(arg: any): number {
  if (Array.isArray(arg)) {
    return arg.length;
  }
  return 1;
}

/**
 * Check if the user agent is a mobile device
 * @returns {boolean} True if mobile, False otherwise
 */
export function isMobileBrowser(): boolean {
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  ) {
    return true;
  }
  return false;
}

/**
 * Replaces placeholders ${key} contained in the text with the value from the valueMap entries.
 * For example:
 * let text = "The domain ${domain} may be for sale.";
 * let valueMap = {}; valueMap["domain"] = "awesome.com";
 * let replaced = substitutePlaceholders(text, valueMap);
 * @param templateString
 * @param valueMap
 * @returns string
 */
export function substitutePlaceholders(
  templateString: string | undefined,
  valueMap: Record<string, string>
): string | undefined {
  if (templateString === undefined) return undefined;

  for (const key in valueMap) {
    if (Object.prototype.hasOwnProperty.call(valueMap, key)) {
      const placeholder = "${" + key + "}"; // This must be a regexp, otherwise only replaces the first occurrence
      const regexp = new RegExp(escapeRegExp(placeholder), "g");
      templateString = templateString.replace(regexp, valueMap[key]);
    }
  }
  return templateString;
}

// eslint-disable-next-line id-length
export function substitutePlaceholdersWithoutDollar(
  templateString: string | undefined,
  valueMap: Record<string, string>
): string | undefined {
  if (templateString === undefined) return undefined;

  for (const key in valueMap) {
    if (Object.prototype.hasOwnProperty.call(valueMap, key)) {
      const placeholder = "{" + key + "}"; // This must be a regexp, otherwise only replaces the first occurrence
      const regexp = new RegExp(escapeRegExp(placeholder), "g");
      templateString = templateString.replace(regexp, valueMap[key]);
    }
  }
  return templateString;
}

/**
 * Escapes input that is to be treated as a literal string within a regular expression
 * that would otherwise be mistaken for a special character.
 * See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
 * @param string
 * @returns string
 */
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

/**
 * The parameters of the function above are the name of the cookie (cname), the value of
 * the cookie (cvalue), and the number of hours until the cookie should expire (exhours).
 * The function sets a cookie by adding together the cookiename, the cookie value, and
 * the expires string.
 * @param cname
 * @param cvalue
 * @param ttl
 */
export function setCookie(cname: string, cvalue: string, ttl: number): void {
  const d = new Date();
  d.setTime(d.getTime() + ttl);
  const expires = "expires=" + d.toUTCString();
  document.cookie = cname + "=" + cvalue + ";" + expires + ";path=/";
}

/**
 * The parameters of the function above are the name of the cookie (cname), the value of
 * the cookie (cvalue), and the number of hours until the cookie should expire (exhours).
 * The function sets a cookie by adding together the cookiename, the cookie value, and
 * the expires string.
 * @param cname
 * @returns {string}
 */
export function getCookie(cname: string): string {
  // Handle edge case when the function is called on server side or executed by a bot with no document.cookie.
  if (!document?.cookie || typeof document.cookie !== "string") return "";

  const name = cname + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

/**
 * Return the protocol of the current URL in UPPER CASE
 * @returns {string}
 */
export function getUrlProtocol(): string {
  return window.location.protocol.replace(/:$/, "").toUpperCase();
}

/**
 * Returns UUID (this might not be universally unique but serves our purpose)
 * @returns {string}
 */
export function generateUUID(): string {
  let d = new Date().getTime();
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    d += performance.now(); // use high-precision timer if available
  }

  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (d + Math.random() * 16) % 16 | 0;
    d = Math.floor(d / 16);
    // eslint-disable-next-line no-mixed-operators
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/**
 * This function checks if the parking visitor cookie is set by calling the getCookie function.
 * If the cookie is set it will return its current value.
 * If the cookie is not set, it will create a new visitor identifier, and store the cookie
 * for x number of hours, by calling the setCookie function.
 * @returns {string}
 */
export function getVisitorIdentifier(): string {
  let visitorId = getCookie(LanderCookie.VISITOR_ID);
  if (!visitorId) {
    visitorId = generateUUID();
  }

  const cookieTtl = 365 * 86400000; // 1 year (365 * 24 * 60 * 60 * 1000)

  // set/reset cookie time
  setCookie(LanderCookie.VISITOR_ID, visitorId, cookieTtl);
  return visitorId;
}

/**
 * Time zone objects containing the offset from UTC and a city for info
 * (example: UTC time is 10:00, MST time is 03:00 => offset is -7)
 */
export const TimeZone: Record<string, TimeZoneObject> = {
  MST: {
    offset: -7,
    city: "Phoenix",
  },
};

/**
 *
 * @param timeZone use the TimeZone in HelperFunctions
 * @param format such as 'yyyy:MM:dd HH:mm:ss'
 * @returns {*}
 */
export function getTime(timeZone: TimeZoneObject, format: string): string {
  return getFormattedTime(getDate(timeZone.offset), format);
}

interface DateSymbols {
  yyyy: number;
  MM: string;
  dd: string;
  HH: string;
  mm: string;
  ss: string;
  [key: string]: string | number;
}

export function getFormattedTime(date: Date, format: string): string {
  const symbol: DateSymbols = {
    yyyy: date.getFullYear(),
    MM:
      date.getMonth() < 10
        ? "0" + (date.getMonth() + 1)
        : "" + (date.getMonth() + 1),
    dd: date.getDate() < 10 ? "0" + date.getDate() : "" + date.getDate(),
    HH: date.getHours() < 10 ? "0" + date.getHours() : "" + date.getHours(),
    mm:
      date.getMinutes() < 10 ? "0" + date.getMinutes() : "" + date.getMinutes(),
    ss:
      date.getSeconds() < 10 ? "0" + date.getSeconds() : "" + date.getSeconds(),
  };

  const arr = format.split("");
  const symbols: string[] = [];
  let prevChar: string | null = null;
  let currChar: string;
  let currSym = "";
  for (let i = 0; i < arr.length; i++) {
    currChar = arr[i];
    prevChar === null ? (prevChar = "") : (prevChar = arr[i - 1]);
    if (currChar !== prevChar) {
      currSym += prevChar;
      if (currSym !== "") {
        symbols.push(currSym);
      }
      currSym = "";
    } else {
      currSym += currChar;
    }
  }
  symbols.push(prevChar + currSym);

  const elements = symbols.map((s) => {
    if (s in symbol) {
      return symbol[s];
    }
    return s;
  });

  return elements.join("");
}

/**
 * Returns a new date calculated with the offset from the UTC time (see TimeZone)
 * @param offset
 * @returns {Date}
 */
export function getDate(offset: number): Date {
  // current date and time, specified in the local time zone
  const d = new Date();

  // convert date to milliseconds, add local time zone offset and get UTC time in milliseconds
  const utc = d.getTime() + d.getTimezoneOffset() * 60000;

  // create new Date object for different city using supplied offset
  return new Date(utc + 3600000 * offset);
}

/**
 * Returns a title cased string. Source: https://stackoverflow.com/a/196991
 * @param {String} str
 * @returns {String}
 */
export function toTitleCase(str: string): string {
  return str.replace(/\w\S*/g, (txt) => {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
  });
}

export function isParkWeb(landerConfig: LanderConfig): boolean {
  return (
    (landerConfig.system === System.PARKWEB && !landerConfig.turnKeyReseller) ||
    false
  );
}

// https://ec.europa.eu/eurostat/statistics-explained/index.php?title=Glossary:Country_codes
const CONSENT_REQUIRED_COUNTRIES_ISO = [
  // Countries in EEA and EU
  "BE",
  "EL",
  "LT",
  "PT",
  "BG",
  "ES",
  "LU",
  "RO",
  "CZ",
  "FR",
  "HU",
  "SI",
  "DK",
  "HR",
  "MT",
  "SK",
  "DE",
  "IT",
  "NL",
  "FI",
  "EE",
  "CY",
  "AT",
  "SE",
  "IE",
  "LV",
  "PL",
  // Countries in EEA, but not in EU
  "IS",
  "NO",
  "LI",
  "CH",
  // UK
  "GB",
];

/**
 * Returns true if user is in a region that requires consent (EEA/EU/UK)
 * Used for analytics tracking
 */
export const getIsConsentRequired = (): boolean => {
  const params = new URLSearchParams(location.search);
  if (params.get("forceCMCountry")) return true;

  const countryCode = getCookie("country");
  return CONSENT_REQUIRED_COUNTRIES_ISO.includes(countryCode);
};
